package com.gsb.vyapar.bhagya.presentation.common.edittext

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.OutlinedTextField
import androidx.compose.material.TextFieldColors
import androidx.compose.material.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color.Companion.Black
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gsb.vyapar.bhagya.theme.*
import com.gsb.vyapar.bhagya.util.*
import org.jetbrains.compose.resources.painterResource
import com.gsb.vyapar.bhagya.presentation.common.text.Text12Regular
import com.gsb.vyapar.bhagya.presentation.common.text.Text14Medium
import com.gsb.vyapar.bhagya.presentation.common.text.Text14MediumI

@Composable
fun getEditTextColor(): TextFieldColors {
    return TextFieldDefaults.outlinedTextFieldColors(
        backgroundColor = white,
        textColor = Black,
        cursorColor = Black,
        focusedBorderColor = focussed,
        unfocusedBorderColor = focussed,
        unfocusedLabelColor = unfocussed,
        focusedLabelColor = message
    )
}

@Composable
fun EmailEditText(
    text: String,
    enabled: Boolean = true,
    label: String = "Enter Email",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(label, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isEmailError(text),
            enabled = enabled,
        )
        if (isEmailError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Email",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun PasswordEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current
    var passwordVisible by rememberSaveable { mutableStateOf(false) }
    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
            trailingIcon = {
                val painter = painterResource(if (passwordVisible) visibility_on else visibility_off)
                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                    Icon(painter = painter, "password")
                }
            },
            isError = isPasswordError(text)
        )
        if (isPasswordError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid password",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun FirstNameEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter first name", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isFirstNameError(text)
        )
        if (isFirstNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid first name",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun LastNameEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter last name", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isLastNameError(text)
        )
        if (isLastNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid last name",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun BusinessNameEditText(
    text: String,
    enabled: Boolean = true,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter business name *", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isBusinessNameError(text),
            enabled = enabled,
        )
        if (isBusinessNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid business name",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun GotraEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Gotra", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isLastNameError(text)
        )
        if (isLastNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Gotra",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun KuldevEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Kuldev", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isLastNameError(text)
        )
        if (isLastNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Kuldev",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun UserIdEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("User ID", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
        )
    }
}

@Composable
fun MobileUserIdEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter mobile number or user id", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isMobileUserIdError(text)
        )
        if (isMobileUserIdError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = if (text.all { it.isLetter() }) "UserId must be exactly 10 characters long, Only alphabets (A-Z, a-z) are allowed." else if (text.toDoubleOrNull() != null)  "Enter valid mobile number" else "Enter valid mobile number or UserId must be exactly 10 characters long, Only alphabets (A-Z, a-z) are allowed.",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun UserIdPasswordEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current
    var passwordVisible by rememberSaveable { mutableStateOf(false) }
    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Password", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
            trailingIcon = {
                val painter = painterResource(if (passwordVisible) visibility_on else visibility_off)
                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                    Icon(painter = painter, "password")
                }
            },
            isError = isUserIdPasswordError(text)
        )
        if (isUserIdPasswordError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Password must be exactly 10 characters long",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun CategoryNameEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isFirstNameError(text)
        )
        if (isFirstNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Category name",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun MobileNumberEditText(
    text: String,
    enabled: Boolean = true,
    label: String = "Enter mobile number",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(label, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isMobileError(text),
            enabled = enabled,
        )
        if (isMobileError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid mobile number",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun OtpEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter OTP", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isOtpError(text)
        )
        if (isOtpError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid OTP",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun PincodeEditText(
    text: String,
    hideKeypadAfterSuccess: Boolean = false,
    enabled: Boolean = true,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter pincode *", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isPincodeError(text),
            enabled = enabled,
        )
        if (isPincodeError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid pincode",
                    color = MaterialTheme.colors.error,
                )
            }
        } else {
            if (hideKeypadAfterSuccess && text.isNotEmpty()) {
                keyboardController?.hide()
            }
        }
    }
}

@Composable
fun GstEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isGstError(text)
        )
        if (isGstError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid GST number",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun AdhaarPanEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isAdhaarOrPanError(text)
        )
        if (isAdhaarOrPanError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid adhar/PAN",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun BusinessNameEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isFirstNameError(text)
        )
        if (isFirstNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid business name",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun ZipcodeEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isZipcodeError(text)
        )
        if (isZipcodeError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid pincode",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun AddressEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isAddressError(text)
        )
        if (isAddressError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid address",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun CityEditText(
    text: String,
    hint: String = "",
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(hint, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isCityError(text)
        )
        if (isCityError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid city",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun FbEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Facebook link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isFbError(text)
        )
        if (isFbError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Facebook link",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun InstaEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Instagram link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isInstaError(text)
        )
        if (isInstaError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Instagram link",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun YoutubeEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Youtube link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isYoutubeError(text)
        )
        if (isYoutubeError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Youtube link",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun LinkedInEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Linkedin link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isLinkedInError(text)
        )
        if (isLinkedInError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Linkedin link",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun XEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter X link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isXError(text)
        )
        if (isXError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid X link",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun DetailEditText(
    label: String,
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(label, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isDetailError(text)
        )
        if (isDetailError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter not more than 4000 characters",
                    color = MaterialTheme.colors.error,
                )
            }
        }
        Row(horizontalArrangement = Arrangement.End, modifier = Modifier.fillMaxWidth()) {
            Text12Regular("(${text.length}/4000)", black)
        }
    }
}

@Composable
fun AddressEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter business address *", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isAddressError(text)
        )
        if (isAddressError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter not more than 320 characters",
                    color = MaterialTheme.colors.error,
                )
            }
        }
        Row(horizontalArrangement = Arrangement.End, modifier = Modifier.fillMaxWidth()) {
            Text12Regular("(${text.length}/320)", black)
        }
    }
}

@Composable
fun WebsiteEditText(
    label: String,
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(label, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isWebsiteError(text)
        )
        if (isWebsiteError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid link address",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun GMapEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Google Map link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isGMapError(text)
        )
        if (isGMapError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Google Map link address",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun AndroidEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter Android link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isAndroidError(text)
        )
        if (isAndroidError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Android link",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun IOSEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter iOS link", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isIosError(text)
        )
        if (isIosError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid iOS link",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun MqttUrlEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter MQTT url", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isMqttUrlError(text)
        )
        if (isMqttUrlError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid MQTT url",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun MqttUserNameEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter MQTT username", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isMqttUserNameError(text)
        )
        if (isMqttUserNameError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid MQTT username",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun MqttPasswordEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter MQTT password", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isMqttPasswordError(text)
        )
        if (isMqttPasswordError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid MQTT password",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun MqttCertificateEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter MQTT certificate", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isMqttCertificateError(text)
        )
        if (isMqttCertificateError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid MQTT certificate",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun PortEditText(
    label: String,
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(label, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isPortError(text)
        )
        if (isPortError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid Port number",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun AppVersionEditText(
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter App Version", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isAppVersionError(text)
        )
        if (isAppVersionError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid App Version",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun VersionEditText(
    label: String,
    text: String,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium(label, hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isNumberError(text)
        )
        if (isNumberError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid version number",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun LatitudeEditText(
    text: String,
    enabled: Boolean = true,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter latitude *", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isLatitudeError(text),
            enabled = enabled
        )
        if (isLatitudeError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid latitude",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun LongitudeEditText(
    text: String,
    enabled: Boolean = true,
    onTextChange: (String) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Column {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = text,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Enter longitude *", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            isError = isLongitudeError(text),
            enabled = enabled
        )
        if (isLongitudeError(text)) {
            Spacer(Modifier.height(4.dp))
            Row {
                Spacer(Modifier.width(16.dp))
                Text12Regular(
                    text = "Enter valid longitude",
                    color = MaterialTheme.colors.error,
                )
            }
        }
    }
}

@Composable
fun SearchEditText(
    value: TextFieldValue,
    modifier: Modifier = Modifier,
    onTextChange: (TextFieldValue) -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current
    Column {
        OutlinedTextField(
            modifier = modifier.fillMaxWidth(),
            shape = RoundedCornerShape(8.dp),
            value = value,
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = { onTextChange(it) },
            label = { Text14Medium("Search by Name/Pincode/Reg-Number/Category/SubCategory etc.", hintColor) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
        )
    }
}

@Composable
fun HomeSearchEditText(onClick: () -> Unit) {
    val keyboardController = LocalSoftwareKeyboardController.current
    Column(Modifier.clickable { onClick() }) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth().clickable { onClick() },
            shape = RoundedCornerShape(8.dp),
            value = "",
            singleLine = true,
            textStyle = TextStyle(
                fontFamily = interMedium(),
                fontSize = 14.sp,
                fontWeight = FontWeight(500)
            ),
            onValueChange = {  },
            label = { Text14MediumI("Search businesses, products, or services...", black, Modifier.fillMaxWidth()) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
            colors = getEditTextColor(),
            enabled = false
        )
    }
}
